// Temporary diagnostic — dumps a summary of the DModel JSON Unity hands us
// so we can see whether materials are missing from Unity's side or being
// dropped by our DModel → GLB conversion. Wired by RenderSceneModal and
// DownloadModelModal; remove both call sites once the materials gap is
// understood. Console-only; nothing leaves the browser.

interface DModelMatColor { id?: string; hex?: string; }
interface DModelMatTex   { id?: string; uri?: string; url?: string; }
interface DModelMatFloat { id?: string; val?: number; }
interface DModelMat {
  name?: string;
  colors?: DModelMatColor[];
  textures?: DModelMatTex[];
  floats?: DModelMatFloat[];
}
interface DModelNode {
  name?: string;
  mesh?: number;
  materials?: number[];
}
interface DModelPayload {
  materials?: DModelMat[];
  meshes?: { name?: string; uri?: string }[];
  nodes?: DModelNode[];
}

export function logDModelSummary(json: string): void {
  let parsed: DModelPayload;
  try {
    parsed = JSON.parse(json);
  } catch (err) {
    console.warn("[SceneDesigner DModel] couldn't parse Designer payload as JSON:", err);
    return;
  }

  const materials = parsed.materials ?? [];
  const meshes    = parsed.meshes    ?? [];
  const nodes     = parsed.nodes     ?? [];

  console.groupCollapsed(
    `[SceneDesigner DModel] materials=${materials.length} meshes=${meshes.length} nodes=${nodes.length}`
  );

  // Top-level material summary — each material's id-typed fields (Unity
  // shader property names like _Color, _MainTex). This is what
  // MaterialBuilderCache reads when building the GLB material.
  if (materials.length === 0) {
    console.warn("⚠ materials array is empty — Designer emitted no top-level materials, so the GLB will fall back to white defaults.");
  } else {
    console.log("Materials:");
    materials.forEach((m, i) => {
      console.log(`  [${i}] name="${m.name ?? ""}"`);

      // Colors — useful to see the actual _Color hex.
      const colors = m.colors ?? [];
      if (colors.length === 0) {
        console.log("    colors: (none)");
      } else {
        console.log("    colors:");
        colors.forEach(c => console.log(`      ${c.id} = ${c.hex}`));
      }

      // Textures — this is the one we suspect. Inline whether each texture
      // has uri / url / neither, and a slice of the uri so we can confirm
      // it's a data:image base64 (what SaveBase64ImagesToTemp can handle)
      // rather than a dashboard asset URL.
      const textures = m.textures ?? [];
      if (textures.length === 0) {
        console.log("    textures: (none)");
      } else {
        console.log("    textures:");
        textures.forEach(t => {
          const source = t.uri ? "uri" : t.url ? "url" : "(neither)";
          const value  = t.uri ?? t.url ?? "";
          const preview = value.length > 70 ? value.slice(0, 70) + "…" : value;
          const lenNote = value ? ` (${value.length} chars)` : "";
          console.log(`      ${t.id}  [${source}]${lenNote}  ${preview}`);
        });
      }

      // Floats — _Metallic / _Glossiness / _BumpScale values.
      const floats = m.floats ?? [];
      if (floats.length === 0) {
        console.log("    floats: (none)");
      } else {
        console.log("    floats: " + floats.map(f => `${f.id}=${f.val}`).join(", "));
      }
    });
  }

  // Per-node material assignment — empty `node.materials` means the
  // GLB exporter won't add any primitives for that node's mesh.
  if (nodes.length > 0) {
    console.log("Nodes:");
    nodes.forEach((n, i) => {
      const mats = JSON.stringify(n.materials ?? []);
      console.log(`  [${i}] mesh=${n.mesh} materials=${mats} name="${n.name ?? ""}"`);
    });
  }

  console.groupEnd();
}
